// The Clearance & RDN Renewals page's reading of GET /clearance-renewals.
// Pure functions, so the page and its tests agree on what counts as in
// flight, overdue, coming up or lapsed.
//
// A renewal Action is open until somebody in the PMO marks it done, or the
// daily sweep closes it because a new expiry date was recorded, the date was
// removed, or the person left (resolved_by_system). An expiry with no Action
// behind it is either still to come (the sweep raises it on its opens_on day,
// which the server works out) or has passed with nothing raised.

export const RENEWAL_TABS = [
  { id: 'in-flight', label: 'In flight' },
  { id: 'coming-up', label: 'Coming up' },
  { id: 'completed', label: 'Completed' },
  { id: 'calendar',  label: 'Calendar' },
];

export const KIND_FILTERS = [
  { id: 'all',       label: 'All' },
  { id: 'clearance', label: 'Clearance' },
  { id: 'site_pass', label: 'Site pass' },
  { id: 'rdn',       label: 'RDN' },
];

// Each state carries its own words, so a chip reads without its colour.
export const STATES = {
  overdue:     { label: 'Overdue',          tone: 'bg-red-50 text-red-700 border-red-200' },
  in_progress: { label: 'In progress',      tone: 'bg-blue-50 text-blue-700 border-blue-200' },
  not_started: { label: 'Not started',      tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  done:        { label: 'Done',             tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  closed:      { label: 'Closed by record', tone: 'bg-gray-100 text-gray-600 border-gray-200' },
  upcoming:    { label: 'Coming up',        tone: 'bg-white text-gray-600 border-gray-200' },
  lapsed:      { label: 'Lapsed',           tone: 'bg-orange-50 text-orange-700 border-orange-200' },
};

const DAY = 86400000;
const toUtc = (iso) => Date.parse(`${String(iso).slice(0, 10)}T00:00:00Z`);

/** Whole days from `today` to `iso`; negative once it has passed. */
export function daysUntil(iso, today) {
  return Math.round((toUtc(iso) - toUtc(today)) / DAY);
}

export function addDays(iso, n) {
  return new Date(toUtc(iso) + n * DAY).toISOString().slice(0, 10);
}

/** "in 12 days", "today", "3 days ago". */
export function relativeDays(iso, today) {
  const n = daysUntil(iso, today);
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  if (n === -1) return 'yesterday';
  return n > 0 ? `in ${n} days` : `${-n} days ago`;
}

/** Who on the PMO has started it and not finished their part. */
export const startedBy = (a) => (a.assignees || []).filter(p => p.started_at && !p.completed_at);

/** One state for a renewal Action. Overdue wins over in progress: the date has gone either way. */
export function actionState(a, today) {
  if (a.status !== 'open') return a.resolved_by_system ? 'closed' : 'done';
  if (a.due_date && a.due_date < today) return 'overdue';
  return startedBy(a).length ? 'in_progress' : 'not_started';
}

/** Marked done by a person, but their People record still carries the date that was renewed. */
export const recordStillOld = (a) =>
  a.status !== 'open' && !a.resolved_by_system && !a.left && !!a.current_expiry && a.current_expiry === a.due_date;

export function matches(item, { kind = 'all', search = '' } = {}) {
  if (kind !== 'all' && item.kind !== kind) return false;
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return [item.person_name, item.label, item.designation, item.team_name].some(v => v && String(v).toLowerCase().includes(q));
}

export const inFlight = (data, today, f) =>
  (data?.actions || []).filter(a => a.status === 'open' && matches(a, f))
    .sort((x, y) => (x.due_date || '').localeCompare(y.due_date || ''))
    .map(a => ({ ...a, state: actionState(a, today) }));

export const completed = (data, today, f) =>
  (data?.actions || []).filter(a => a.status !== 'open' && matches(a, f))
    .sort((x, y) => (y.resolved_at || '').localeCompare(x.resolved_at || ''))
    .map(a => ({ ...a, state: actionState(a, today) }));

const key = (personId, kind, date) => `${personId}|${kind}|${date}`;

/**
 * Current expiry dates with no renewal Action raised for that date: those
 * still to come, with the day the sweep will raise one, and those that have
 * passed with nothing raised.
 */
export function unraised(data, today, f) {
  const raised = new Set((data?.actions || []).map(a => key(a.person_id, a.kind, a.due_date)));
  const out = { upcoming: [], lapsed: [] };
  for (const e of data?.expiries || []) {
    if (raised.has(key(e.person_id, e.kind, e.expiry)) || !matches(e, f)) continue;
    if (e.expiry < today) out.lapsed.push({ ...e, state: 'lapsed' });
    else {
      const opens = e.opens_on || today;
      out.upcoming.push({ ...e, state: 'upcoming', opens_on: opens < today ? today : opens });
    }
  }
  out.upcoming.sort((x, y) => x.expiry.localeCompare(y.expiry));
  out.lapsed.sort((x, y) => y.expiry.localeCompare(x.expiry));
  return out;
}

/** The tiles across the top. Counts ignore the filters, so they always describe everything. */
export function summary(data, today) {
  const open = inFlight(data, today, {});
  const { upcoming, lapsed } = unraised(data, today, {});
  const since = addDays(today, -90);
  const soon = addDays(today, 90);
  return {
    inFlight: open.length,
    overdue: open.filter(a => a.state === 'overdue').length,
    notStarted: open.filter(a => a.state === 'not_started').length,
    comingUp: upcoming.filter(e => e.expiry <= soon).length,
    lapsed: lapsed.length,
    completed90: completed(data, today, {}).filter(a => (a.resolved_at || '').slice(0, 10) >= since).length,
  };
}

/**
 * Everything with a date, for the calendar: every renewal Action on the
 * expiry it was raised for, and every current expiry with no Action behind it.
 * `ref` says which panel a chip opens.
 */
export function calendarEntries(data, today, f) {
  const actions = (data?.actions || []).filter(a => a.due_date && matches(a, f)).map(a => ({
    date: a.due_date, person_name: a.person_name, kind: a.kind, label: a.label,
    state: actionState(a, today), ref: { type: 'action', id: a.id },
  }));
  const { upcoming, lapsed } = unraised(data, today, f);
  const loose = [...upcoming, ...lapsed].map(e => ({
    date: e.expiry, person_name: e.person_name, kind: e.kind, label: e.label,
    state: e.state, ref: { type: 'expiry', id: key(e.person_id, e.kind, e.expiry) },
  }));
  return [...actions, ...loose].sort((x, y) => x.date.localeCompare(y.date) || x.person_name.localeCompare(y.person_name));
}

export const expiryRef = (e) => key(e.person_id, e.kind, e.expiry);

/**
 * The weeks of a month, Monday first, as ISO dates with null either side of
 * the month. `month` is 0 to 11.
 */
export function monthWeeks(year, month) {
  const first = new Date(Date.UTC(year, month, 1));
  const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const lead = (first.getUTCDay() + 6) % 7;
  const cells = Array(lead).fill(null);
  for (let d = 1; d <= days; d++) cells.push(new Date(Date.UTC(year, month, d)).toISOString().slice(0, 10));
  while (cells.length % 7) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}
